import {SmallLoader} from '@kit';

import React from 'react';

import {useOriginQuery} from '../../../generated/types';
import {PageTools} from '../../../components/page-tools';
import {getApolloError, humanEnumeration, getSourceText} from '../../../components/utils';
import {Markdown} from '../../../components/utils/md';
import {EditHoc} from '../../../components/edit-hoc';
import {ImageTarget, type Origin} from '../../../generated/types';
import {Gallery} from '../../../components/gallery';
import {OtherTitles} from '../../../components/other-titles';
import {NotFoundSeo, isNotFoundError, useNotFound} from '../../../lib/seo';
import {OriginSeo} from '../seo';
import {NotFound} from '../../../components/not-found';
import {CommentTargetType, Comments} from '../../../components/comments';

import dashboardCss from '@kit/sidebar/dashboard.module.css';
import pageCss from '../../../components/common.module.css';
import markdownCss from '../../../components/utils/md/markdown.module.css';
import css from './origin-view.module.css';

type Props = {
	originId: string;
	title: string;
};

export const OriginView: React.FC<Props> = ({originId, title}) => {
	const {data, loading, error} = useOriginQuery({variables: {id: originId}});
	const origin = data?.origin;
	const notFound = !loading && !origin && isNotFoundError(error);
	useNotFound(notFound);
	const mainImage = origin?.images?.at(0);

	return (
		<article className={dashboardCss.readingWrapper}>
			{origin && <OriginSeo origin={origin as Origin} />}
			{notFound && <NotFoundSeo />}
			<PageTools />
			<h1 className={dashboardCss.mainTitle}>{title}</h1>
			<div className={dashboardCss.page}>
				{loading && (
					<div className={pageCss.fullSizeBlock}>
						<SmallLoader />
					</div>
				)}
				{notFound ? (
					<NotFound />
				) : (
					error && <div className={pageCss.fullSizeBlock}>{getApolloError(error).message}</div>
				)}
				{origin && (
					<div>
						{mainImage && (
							<div className={css.image}>
								<img
									src={`/images/${mainImage.path}`}
									width={mainImage.width}
									height={mainImage.height}
									alt={mainImage.description || origin.title}
								/>
							</div>
						)}
						<EditHoc
							load={() => import('../edit-buttons').then((m) => m.EditOrigin)}
							editProps={{id: origin.id}}
						>
							<Markdown
								text={`
						[indent]*Характеристики:* ${origin.abilities}
*Черта:* ${origin.feat}
*Умения в навыках:* ${origin.skills}
*Умения в инструментах:* ${origin.instruments}
*Экипировка:* ${origin.equipment}[/indent]
`}
							/>

							<Markdown text={origin.description} />

							<div className={markdownCss.margin}>
								<div className={markdownCss.indent}>
									<OtherTitles
										originalTitle={origin.originalTitle}
										alternativeTitles={origin.alternativeTitles}
									/>
									{origin.source && (
										<p>
											<b>Источник: </b>
											{getSourceText(origin.source as any)}.
										</p>
									)}
								</div>
							</div>
						</EditHoc>
					</div>
				)}
				{origin && <Gallery images={origin.images || []} target={{type: ImageTarget.Origin, id: originId}} />}
				{origin && <Comments targetType={CommentTargetType.Origin} targetId={originId} />}
			</div>
		</article>
	);
};
